import React, { Suspense, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { useGLTF, Float, OrbitControls, ContactShadows, Sparkles } from "@react-three/drei";

const TARGET_SIZE = 3.0;

function Model({ src }) {
  const { scene } = useGLTF(src);
  const groupRef = useRef();

  useEffect(() => {
    scene.traverse((o) => {
      if (o.isMesh && o.material) {
        const m = o.material;
        m.transparent = false;
        m.opacity = 1;
        m.depthWrite = true;
        m.side = THREE.FrontSide;
        if ("transmission" in m) m.transmission = 0;
        if ("thickness" in m) m.thickness = 0;
        m.needsUpdate = true;
      }
    });
    const box = new THREE.Box3().setFromObject(scene);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);
    scene.position.sub(center);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    if (groupRef.current) groupRef.current.scale.setScalar(TARGET_SIZE / maxDim);
  }, [scene]);

  return (
    <group ref={groupRef}>
      <primitive object={scene} />
    </group>
  );
}

class ThreeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: false };
  }
  static getDerivedStateFromError() {
    return { error: true };
  }
  render() {
    if (this.state.error) return this.props.fallback;
    return this.props.children;
  }
}

const FallbackImage = ({ src }) => (
  <img
    src={src}
    alt="Baby Hemp product"
    className="w-full max-w-md mx-auto object-contain drop-shadow-[0_30px_40px_rgba(111,80,58,0.12)]"
    loading="lazy"
  />
);

export const Product3DViewer = ({
  src,
  fallbackImage,
  float = true,
  autoRotate = true,
  heightClass = "h-[440px]",
  testId = "product-3d",
  sparkles = false,
}) => {
  const [supported, setSupported] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    try {
      const canvas = document.createElement("canvas");
      const gl = canvas.getContext("webgl2") || canvas.getContext("webgl");
      setSupported(!!gl);
    } catch {
      setSupported(false);
    }
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  if (!supported) {
    return (
      <div className="w-full flex items-center justify-center" data-testid={`${testId}-fallback`}>
        <FallbackImage src={fallbackImage} />
      </div>
    );
  }

  const model = <Model src={src} />;

  return (
    <div className={`w-full ${heightClass} touch-none`} data-testid={testId}>
      <ThreeErrorBoundary fallback={<FallbackImage src={fallbackImage} />}>
        <Canvas dpr={[1, 1.5]} camera={{ position: [0, 0, 5], fov: 38 }} gl={{ antialias: true, alpha: true }}>
          <ambientLight intensity={0.75} />
          <hemisphereLight intensity={0.5} groundColor="#E9E2D6" color="#ffffff" />
          <directionalLight position={[4, 6, 6]} intensity={2.0} />
          <directionalLight position={[-6, 2, -3]} intensity={0.7} color="#B8C4A3" />
          {sparkles && <pointLight position={[0, -0.5, 3]} intensity={1.1} color="#fff3e0" distance={9} />}
          <Suspense fallback={null}>
            {float ? (
              <Float speed={1.1} rotationIntensity={0.18} floatIntensity={0.45} floatingRange={[-0.05, 0.05]}>
                {model}
              </Float>
            ) : (
              model
            )}
            {sparkles && (
              <>
                <Sparkles count={90} scale={[5.5, 6.5, 5.5]} size={2.6} speed={0.32} noise={0.5} opacity={0.95} color="#fff1cf" />
                <Sparkles count={45} scale={[3.6, 4.8, 3.6]} size={4.2} speed={0.24} noise={0.3} opacity={0.8} color="#fff8ec" />
                <Sparkles count={20} scale={[2.4, 3.2, 2.4]} size={6} speed={0.18} noise={0.2} opacity={0.6} color="#ffffff" />
              </>
            )}
            {!isMobile && (
              <ContactShadows position={[0, -1.8, 0]} opacity={0.2} scale={9} blur={3} far={4} color="#6F503A" />
            )}
          </Suspense>
          <OrbitControls
            enablePan={false}
            enableZoom={false}
            autoRotate={autoRotate}
            autoRotateSpeed={0.9}
            minPolarAngle={Math.PI / 3}
            maxPolarAngle={Math.PI / 1.7}
          />
        </Canvas>
      </ThreeErrorBoundary>
    </div>
  );
};

useGLTF.preload("/assets/balm.glb");
useGLTF.preload("/assets/pouch.glb");
